// Scale bridge dashboard - the headline numbers above the table.
//
// These are stat tiles, not charts, and deliberately: each answers one question
// with one number. Wrapping a single figure in a plot would add ink without
// adding information.
//
// ─── Loading is a skeleton, not a dash ─────────────────────────────────
// Before the first fetch lands, every tile used to read "—", which is the same
// glyph the app uses for "the printer did not send this". So an operator opening
// the dashboard saw four tiles asserting that today's figures were missing, for
// as long as the first request took. A skeleton says "not yet" and cannot be
// mistaken for a value.

import { Card, Col, Row, Skeleton, Tooltip, Typography } from 'antd';
import {
    FileTextOutlined,
    GoldOutlined,
    WarningOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';

import { BRAND } from '../theme';
import { relative, timestamp, weightTotal } from '../lib/format';
import type { Stats } from '../types';

const { Text } = Typography;

interface Kpi {
    label: string;
    value: string;
    hint: string;
    icon: ReactNode;
    color: string;
    /** Shown on hover where the tile's value is rounded or relative, so the
     *  exact figure is one gesture away rather than lost. */
    title?: string;
    /** Draws the eye only when the number is one somebody has to act on. */
    alert?: boolean;
}

export default function KpiStrip({ stats, loading = false }: { stats: Stats | null; loading?: boolean }) {
    const unparsed = stats?.unparsedWeightCount ?? 0;

    const kpis: Kpi[] = [
        {
            label: 'Weighings today',
            value: stats ? stats.todayCount.toLocaleString('en-IN') : '—',
            hint: stats ? `${stats.totalReadings.toLocaleString('en-IN')} recorded in total` : '',
            icon: <FileTextOutlined />,
            color: BRAND.primary,
        },
        {
            label: 'Net weight today',
            value: stats ? weightTotal(stats.todayWeightKg) : '—',
            hint: 'Sum of parsed totals',
            icon: <GoldOutlined />,
            color: BRAND.accent,
            // The tile rounds to the kilogram because a fractional total is noise
            // at a glance. The unrounded figure still exists, so it is offered
            // rather than thrown away.
            title: stats ? `${stats.todayWeightKg.toLocaleString('en-IN')} kg exactly` : undefined,
        },
        {
            label: 'Last reading',
            value: stats?.lastReadingAt ? relative(stats.lastReadingAt) : 'None yet',
            hint: stats?.devices.length
                ? `${stats.devices.length} device${stats.devices.length === 1 ? '' : 's'} seen`
                : 'No device has reported',
            icon: <ClockCircleOutlined />,
            color: BRAND.primary,
            title: stats?.lastReadingAt ? timestamp(stats.lastReadingAt) : undefined,
        },
        {
            // Surfaced as a KPI because it is the number that says the parser is
            // meeting labels it does not fully understand. It is easy to miss in a
            // table and it is the early warning that the printer's format moved.
            label: 'Unparsed weights',
            value: stats ? unparsed.toLocaleString('en-IN') : '—',
            hint: unparsed > 0 ? 'Check the raw label lines' : 'Every label read cleanly',
            icon: <WarningOutlined />,
            color: unparsed > 0 ? BRAND.danger : BRAND.success,
            alert: unparsed > 0,
        },
    ];

    return (
        <Row gutter={[16, 16]}>
            {kpis.map((kpi) => (
                <Col key={kpi.label} xs={24} sm={12} xl={6}>
                    <Card
                        styles={{ body: { padding: 18 } }}
                        // The one tile that can carry bad news gets an edge in its
                        // own colour. Never the only signal: the icon, the number
                        // and the hint underneath all say it too.
                        style={kpi.alert ? { borderColor: '#fecaca', background: '#fffbfb' } : undefined}
                    >
                        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                            <div
                                aria-hidden
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 10,
                                    flexShrink: 0,
                                    background: `${kpi.color}14`,
                                    color: kpi.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 17,
                                }}
                            >
                                {kpi.icon}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {kpi.label}
                                </Text>

                                {loading ? (
                                    <Skeleton
                                        active
                                        title={false}
                                        paragraph={{ rows: 2, width: ['70%', '90%'] }}
                                        style={{ marginTop: 8 }}
                                    />
                                ) : (
                                    <>
                                        {/* The number wears text ink, not the accent colour - the
                                            swatch beside it already carries the identity. */}
                                        <Tooltip title={kpi.title}>
                                            <div
                                                className="num"
                                                style={{
                                                    fontSize: 24,
                                                    fontWeight: 650,
                                                    lineHeight: 1.2,
                                                    marginTop: 2,
                                                    color: kpi.alert ? BRAND.danger : undefined,
                                                    // Long relative times ("2 months ago") must not
                                                    // push the tile wider than its column.
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {kpi.value}
                                            </div>
                                        </Tooltip>
                                        {kpi.hint && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {kpi.hint}
                                            </Text>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>
                </Col>
            ))}
        </Row>
    );
}
